// packages
import moment from 'moment';

/**
 * Converte uma `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
 *
 * @since 1.0.0
 * @category String
 * @param {string} [string=''] a string para converter.
 * @returns {string} Retorna a string em camelCase.
 * @see lowerCase, kebabCase, snakeCase, startCase, upperCase, upperFirst
 * @example
 *
 * toCamelCase('Foo Bar')
 * => 'fooBar'
 *
 * toCamelCase('foo_bar')
 * => 'fooBar'
 *
 */
const toCamelCase = string => string.replace( /^([A-Z])|[\s-_](\w)/g, ( _, partOne, partTwo ) => {
    if ( partTwo )
        return partTwo.toUpperCase();

    return partOne.toLowerCase();
});

/**
 * Obtém a primeira e última palavra da string.
 *
 * @since 1.0.0
 * @category String
 * @param {String} string
 * @returns {Styring}
 * @see String
 * @example
 *
 * firstLastWords( 'Tiago André Marques São Martinho' );
 * => 'Tiago Martinho'
 *
 */
const firstLastWords = string => string.trim().split( /(\s).+\s/ ).join( '' );

/**
 * Converte as keys de um objecto único ou de vários objectos que estão
 * num array de objectos em [camel case](https://en.wikipedia.org/wiki/CamelCase).
 *
 * @since 1.0.0
 * @category String
 * @param {object || array } data
 * @returns {string} Returns o objecto ou array com as keys em camelCcase
 * @see array, object
 * @example
 *
 * camelizeKeys([{foo_bar:'value'}])
 * => [ { fooBar: value } ]
 *
 * camelizeKeys( { foo_bar: 'value' } )
 * => { fooBar: value }
 *
 */
const camelizeKeys = data => {
    if ( Array.isArray( data ) )
        return data.map( position => camelizeKeys( position ) );

    // a key `information` são campos dínamicos, que serão
    // apresentados directamente ao cliente.
    if ( data !== null && data.constructor === Object ) {
        return Object.keys( data ).reduce(
            ( result, key ) => ({
                ...result,
                [toCamelCase( key )]: key !== 'information' ? camelizeKeys( data[key] ) : data[key]
            }), {}
        );
    }

    return data;
};

/**
 * Validação se o objecto tem determinada key.
 *
 * @since 1.0.0
 * @category Object
 * @param {Object} object
 * @param {String} key
 * @returns {Boolean}
 * @see Oject
 * @example
 *
 * haveProperty({ content: 'value' }, 'content' )
 * => true
 *
 * haveProperty({ data: 'value' }, 'content' )
 * => false
 *
 */
const haveProperty = ( object, key ) => Object.prototype.hasOwnProperty.call( object, key );

/**
 * De forma a tornar o objecto reactivo,
 * é removido a key selecioanda e obtido o restante objecto.
 *
 * @since 1.0.0
 * @category Object
 * @param {String} key
 * @param {Object} object
 * @returns {Object}
 * @see Oject
 * @example
 *
 * deleteKey( 'content', { content: 'value', content2: 'value' } )
 * => { content2: 'value' }
 *
 * deleteKey( 'content', { content: 'value' } )
 * => {}
 *
 */
const deleteKey = ( key, object ) => {
    const { [key]: _, ...rest } = object;

    return rest;
};

/**
 * Verifica se dois array's são iguais, independentemente
 * da ordem de cada um.
 *
 * @since 1.0.0
 * @category Array
 * @param {Array} arrayOne
 * @param {Array} arrayTwo
 * @returns {Boolean}
 * @see Array
 * @example
 *
 * compareTwoArrays( [ 1, 2 ], [ 2, 1 ] );
 * => true
 *
 * compareTwoArrays( [ 1 ], [ 2, 1 ]  );
 * => false
 *
 * compareTwoArrays( [ 1, 3 ], [ 2, 1 ]  );
 * => false
 *
 */
const compareTwoArrays = ( arrayOne, arrayTwo ) => (
    arrayOne.length === arrayTwo.length
    && arrayOne.every( newValue => arrayTwo.includes( newValue ) ) );

/**
 * Transforma uma string ou números decimal
 * para inteiros.
 *
 * Significado do radix - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt#description
 *
 * @param {String} number
 * @param {Number} radix
 * @returns {Number}
 * @since 1.0.0
 * @category Array
 * @author Tiago Martinho
 * @see Array
 * @example
 *
 * transformToInt( '10' );
 * => 10
 *
 * compareTwoArrays( '0xF', 16 );
 * => 1500
 *
 */
const transformToInt = ( number, radix = 10 ) => parseInt( number, radix );

/**
 * Copiar um objecto, removendo a sua
 * reactividade, de forma a quando o objecto
 * principal seja actualizado não afecte o clone.
 *
 * @param {Object} object
 * @returns {Number}
 * @since 1.0.0
 * @category Object
 * @author Tiago Martinho
 * @see Object
 * @example
 *
 *
 */
const cloneObject = object => JSON.parse( JSON.stringify( object ) );

/**
 * Ao passar uma variável, retorn se essa variável é objeto ou não.
 *
 * @param {Object} object
 * @returns {Boolean}
 * @since 1.2.3
 * @author Jorge Monteiro
 *
 */
const isAnObject = object => typeof object === 'object' && !Array.isArray( object ) && object !== null;

/**
 * Ao passar uma data, o formato dessa data e o formato final da data,
 * a data passada é considerada como sendo UTC e é convertida para o
 * fúso horário local, mostrando a data com o formato que foi enviado
 * como parâmetro.
 *
 * @param {String} date
 * @param {String} originFormat
 * @param {String} finalFormat
 * @returns {String}
 * @since 1.2.5
 * @author Jorge Monteiro
 *
 */
const parseUtcToLocalDate = ({ date, originFormat, finalFormat }) => {
    if ( !moment( date, originFormat ).isValid() )
        return false;

    const dateMoment = moment( date, originFormat );
    const dateObject = {
        year: dateMoment.format( 'YYYY' ),
        month: dateMoment.format( 'MM' ),
        day: dateMoment.format( 'DD' ),
        hours: dateMoment.format( 'HH' ),
        minutes: dateMoment.format( 'mm' ),
        seconds: dateMoment.format( 'ss' ) || 0
    };

    const utcDate = Date.UTC( dateObject.year, dateObject.month - 1, dateObject.day, dateObject.hours, dateObject.minutes, dateObject.seconds );

    return moment( new Date( utcDate ) ).format( finalFormat );
};

export {
    toCamelCase,
    camelizeKeys,
    haveProperty,
    deleteKey,
    firstLastWords,
    compareTwoArrays,
    transformToInt,
    cloneObject,
    isAnObject,
    parseUtcToLocalDate
};